import { useRef, useState } from 'react'
import { useImageLightboxOptional } from './ImageLightboxContext'
import SourceBadge from './SourceBadge'
import {
  FiAward,
  FiBookOpen,
  FiExternalLink,
  FiFileText,
  FiImage,
  FiLayers,
  FiPlay,
  FiShield,
} from 'react-icons/fi'

export function looksLikeImageUrl(url) {
  if (!url || typeof url !== 'string') return false
  const p = url.split('?')[0].toLowerCase()
  return /\.(jpg|jpeg|png|gif|webp|avif)(\?|$)/i.test(p)
}

function ArtifactSubtitle({ source, subtitle }) {
  if (source) {
    return (
      <div className="mt-1 flex justify-center">
        <SourceBadge source={source} />
      </div>
    )
  }
  if (!subtitle) return null
  return <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>
}

function ArtifactShell({ label, subtitle, source, children, href, onActivate, footer }) {
  const inner = (
    <div className="group relative flex aspect-[4/5] w-full flex-col overflow-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-b from-[#121a35]/95 to-[#060912] shadow-[0_12px_40px_rgba(0,0,0,0.45)] transition-all duration-300 hover:border-cyan-500/25 hover:shadow-[0_16px_50px_rgba(180,156,245,0.12)]">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.55]"
        style={{
          background:
            'radial-gradient(ellipse 85% 55% at 50% -5%, rgba(180, 156, 245, 0.22), transparent 58%), radial-gradient(ellipse 70% 45% at 80% 100%, rgba(180, 156, 245, 0.12), transparent 55%)',
        }}
      />
      <div className="relative z-[1] flex min-h-0 flex-1 flex-col p-2 sm:p-3">{children}</div>
    </div>
  )

  const labelBlock = (
    <div className="mt-3 text-center">
      <p className="text-sm font-semibold tracking-tight text-white">{label}</p>
      <ArtifactSubtitle source={source} subtitle={subtitle} />
    </div>
  )

  if (href) {
    return (
      <div className="flex flex-col">
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="block outline-none ring-cyan-500/40 focus-visible:ring-2"
        >
          {inner}
        </a>
        {labelBlock}
        {footer}
      </div>
    )
  }

  if (onActivate) {
    return (
      <div className="flex flex-col">
        <button
          type="button"
          onClick={onActivate}
          className="block w-full cursor-zoom-in text-left outline-none ring-cyan-500/40 focus-visible:ring-2"
        >
          {inner}
        </button>
        {labelBlock}
        {footer}
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      {inner}
      {labelBlock}
      {footer}
    </div>
  )
}

function SlabFrame({ children }) {
  return (
    <div className="flex min-h-0 flex-1 items-center justify-center p-1.5 sm:p-2">
      <div
        className="relative h-full w-full overflow-hidden rounded-xl border border-white/[0.07] bg-black/50 shadow-inner"
        style={{ boxShadow: 'inset 0 0 24px rgba(0,0,0,0.45)' }}
      >
        {children}
      </div>
    </div>
  )
}

function VideoInner({ src }) {
  const videoRef = useRef(null)
  const [playing, setPlaying] = useState(false)
  const [unsupported, setUnsupported] = useState(false)

  const markUnsupported = () => {
    const el = videoRef.current
    if (el) {
      el.pause()
      el.removeAttribute('src')
      el.load()
    }
    setUnsupported(true)
    setPlaying(false)
  }

  const tryPlay = () => {
    const el = videoRef.current
    if (!el) return
    if (!el.videoWidth) {
      markUnsupported()
      return
    }
    const playAttempt = el.play()
    if (playAttempt && typeof playAttempt.catch === 'function') {
      playAttempt.catch(() => {})
    }
  }

  if (unsupported) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 p-3 text-center">
        <p className="text-xs font-medium text-white">This video cannot be displayed</p>
        <p className="text-[11px] leading-snug text-gray-400">
          This browser cannot decode the image track. Re-upload as H.264 MP4.
        </p>
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] font-medium text-cyan-400 hover:text-cyan-300 hover:underline"
        >
          Download original
        </a>
      </div>
    )
  }

  return (
    <div className="relative flex min-h-0 flex-1 items-center justify-center">
      <video
        ref={videoRef}
        src={src}
        className="h-full max-h-full w-full rounded-xl object-cover"
        playsInline
        preload="metadata"
        onLoadedMetadata={(e) => {
          if (!e.currentTarget.videoWidth) markUnsupported()
        }}
        onError={markUnsupported}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
      {!playing && (
        <button
          type="button"
          className="absolute inset-0 flex items-center justify-center bg-black/35 transition-colors hover:bg-black/45"
          aria-label="Play video"
          onClick={tryPlay}
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/95 text-[#0f172a] shadow-lg shadow-cyan-500/20 ring-2 ring-cyan-400/30 transition-transform hover:scale-105">
            <FiPlay className="ml-1 h-6 w-6" fill="currentColor" />
          </span>
        </button>
      )}
    </div>
  )
}

/** Static tile preview when click opens the lightbox (no inline controls). */
function VideoTilePreview({ src }) {
  return (
    <div className="relative flex min-h-0 flex-1 items-center justify-center">
      <video
        src={src}
        muted
        playsInline
        preload="metadata"
        className="pointer-events-none h-full max-h-full w-full rounded-xl object-cover"
      />
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/35">
        <span
          className="flex h-14 w-14 items-center justify-center rounded-full bg-white/95 text-[#0f172a] shadow-lg shadow-cyan-500/20 ring-2 ring-cyan-400/30"
          aria-hidden
        >
          <FiPlay className="ml-1 h-6 w-6" fill="currentColor" />
        </span>
      </div>
    </div>
  )
}

function ArtifactImageTile({ label, subtitle, source, src, alt, href }) {
  const lb = useImageLightboxOptional()
  const openZoom = () => lb?.open(src, alt || label)
  if (lb) {
    return (
      <ArtifactShell
        label={label}
        subtitle={subtitle}
        source={source}
        onActivate={openZoom}
        footer={
          href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 text-center text-[11px] font-medium text-cyan-400/85 hover:text-cyan-300 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              Open original
            </a>
          ) : null
        }
      >
        <SlabFrame>
          <img src={src} alt={alt || label} className="h-full w-full object-cover" loading="lazy" />
        </SlabFrame>
      </ArtifactShell>
    )
  }
  return (
    <ArtifactShell label={label} subtitle={subtitle} source={source} href={href}>
      <SlabFrame>
        <img src={src} alt={alt || label} className="h-full w-full object-cover" loading="lazy" />
      </SlabFrame>
    </ArtifactShell>
  )
}

function ArtifactVideoTile({ label, subtitle, source, src }) {
  const lb = useImageLightboxOptional()
  if (lb) {
    return (
      <ArtifactShell
        label={label}
        subtitle={subtitle}
        source={source}
        onActivate={() => lb.open(src, label, { type: 'video' })}
        footer={
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 text-center text-[11px] font-medium text-cyan-400/85 hover:text-cyan-300 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            Open original
          </a>
        }
      >
        <SlabFrame>
          <VideoTilePreview src={src} />
        </SlabFrame>
      </ArtifactShell>
    )
  }
  return (
    <ArtifactShell label={label} subtitle={subtitle} source={source}>
      <VideoInner src={src} />
    </ArtifactShell>
  )
}

function ArtifactLinkTile({ label, subtitle, source, href }) {
  let host = href
  try {
    host = new URL(href).hostname.replace(/^www\./, '')
  } catch {
    /* keep raw */
  }
  return (
    <ArtifactShell label={label} subtitle={subtitle || host} source={source} href={href}>
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 p-3 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10">
          <FiExternalLink className="h-7 w-7 text-cyan-300" aria-hidden />
        </div>
        <p className="line-clamp-3 break-all px-1 text-[11px] leading-snug text-cyan-200/90">
          {href}
        </p>
      </div>
    </ArtifactShell>
  )
}

function ArtifactCertificateTile({ label, subtitle, source, href, previewSrc }) {
  const lb = useImageLightboxOptional()
  const isImg = previewSrc && looksLikeImageUrl(previewSrc)

  if (isImg && lb) {
    return (
      <ArtifactShell
        label={label}
        subtitle={subtitle}
        source={source}
        onActivate={() => lb.open(previewSrc, label)}
        footer={
          href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 text-center text-[11px] font-medium text-cyan-400/85 hover:text-cyan-300 hover:underline"
            >
              Open original
            </a>
          ) : null
        }
      >
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 p-1">
          <SlabFrame>
            <img src={previewSrc} alt="" className="h-full w-full object-contain" loading="lazy" />
          </SlabFrame>
        </div>
      </ArtifactShell>
    )
  }

  return (
    <ArtifactShell label={label} subtitle={subtitle} source={source} href={href}>
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 p-1">
        {isImg ? (
          <SlabFrame>
            <img src={previewSrc} alt="" className="h-full w-full object-contain" loading="lazy" />
          </SlabFrame>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-xl border border-amber-500/20 bg-gradient-to-br from-amber-500/10 to-transparent px-3 py-5">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-400/20 text-amber-200 ring-2 ring-amber-400/35">
              <FiAward className="h-6 w-6" />
            </div>
            <FiShield className="h-7 w-7 text-amber-400/80" />
            <span className="text-center text-[10px] font-semibold uppercase tracking-wider text-amber-100/90">
              Certificate
            </span>
          </div>
        )}
      </div>
    </ArtifactShell>
  )
}

function ArtifactStoryPlaceholder({ label, subtitle, source, href }) {
  return (
    <ArtifactShell label={label} subtitle={subtitle} source={source} href={href}>
      <div className="flex min-h-0 flex-1 flex-col rounded-xl border border-white/10 bg-[#0a1020] p-3">
        <div className="mb-2 flex items-center gap-2 border-b border-white/10 pb-2">
          <FiBookOpen className="h-4 w-4 text-cyan-400/90" />
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Story</span>
        </div>
        <div className="flex flex-1 flex-col gap-2">
          <div className="h-14 rounded-md bg-gradient-to-br from-white/10 to-white/5" />
          <div className="h-2 w-full rounded bg-white/10" />
          <div className="h-2 w-4/5 rounded bg-white/10" />
          <div className="h-2 w-3/5 rounded bg-white/10" />
        </div>
        {href && (
          <div className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-cyan-500/30 bg-cyan-500/10 py-2 text-[11px] font-semibold text-cyan-300">
            Open file <FiExternalLink className="h-3.5 w-3.5" />
          </div>
        )}
      </div>
    </ArtifactShell>
  )
}

function ArtifactGalleryTile({ label, subtitle, source, previewSrc, count, onActivate }) {
  const lb = useImageLightboxOptional()
  const handleActivate = () => {
    if (previewSrc && lb) {
      lb.open(previewSrc, label)
    } else if (onActivate) {
      onActivate()
    }
  }
  return (
    <ArtifactShell label={label} subtitle={subtitle} source={source} onActivate={handleActivate}>
      <div className="relative flex min-h-0 flex-1 items-center justify-center">
        <div className="relative h-[78%] w-[72%]">
          <div
            className="absolute inset-0 rounded-lg border border-white/15 bg-[#1a2040]/90 shadow-lg"
            style={{ transform: 'rotate(-8deg) translate(-4px, 4px)' }}
          />
          <div
            className="absolute inset-0 rounded-lg border border-white/15 bg-[#151a35]/95 shadow-lg"
            style={{ transform: 'rotate(4deg) translate(4px, -2px)' }}
          />
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden rounded-lg border border-cyan-500/20 bg-gradient-to-br from-[#0f172a] to-[#080c1a] shadow-inner">
            {previewSrc ? (
              <img
                src={previewSrc}
                alt=""
                className="h-full w-full object-cover opacity-90"
                loading="lazy"
              />
            ) : (
              <FiImage className="h-12 w-12 text-cyan-400/40" />
            )}
            <div className="absolute bottom-2 right-2 flex items-center gap-1 rounded-md bg-black/65 px-2 py-1 text-[10px] font-bold text-white backdrop-blur-sm">
              <FiLayers className="h-3.5 w-3.5 text-cyan-300" />
              {count ?? 0}
            </div>
          </div>
        </div>
      </div>
    </ArtifactShell>
  )
}

function ArtifactFileTile({ label, subtitle, source, href, icon: Icon = FiFileText }) {
  return (
    <ArtifactShell label={label} subtitle={subtitle} source={source} href={href}>
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-500/15 text-violet-300 ring-1 ring-violet-400/30">
          <Icon className="h-7 w-7" />
        </div>
        <span className="text-center text-xs text-gray-400">Open file</span>
      </div>
    </ArtifactShell>
  )
}

/**
 * Artifact grid (reference style): spotlight, 2–3 columns, labels.
 */
export default function CardArtifactsGrid({ items = [], galleryAnchorId = 'artifact-photo-roll' }) {
  if (!items.length) return null

  const scrollToRoll = () => {
    const el = document.getElementById(galleryAnchorId)
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-5 lg:gap-6">
      {items.map((item) => {
        const key = item.key
        switch (item.type) {
          case 'image':
            return (
              <ArtifactImageTile
                key={key}
                label={item.label}
                subtitle={item.subtitle}
                source={item.source}
                src={item.src}
                alt={item.alt}
                href={item.href}
              />
            )
          case 'video':
            return (
              <ArtifactVideoTile
                key={key}
                label={item.label}
                subtitle={item.subtitle}
                source={item.source}
                src={item.src}
              />
            )
          case 'certificate':
            return (
              <ArtifactCertificateTile
                key={key}
                label={item.label}
                subtitle={item.subtitle}
                source={item.source}
                href={item.href}
                previewSrc={item.previewSrc}
              />
            )
          case 'story': {
            const mk = item.mediaKind
            if (mk === 'image' && item.storySrc) {
              return (
                <ArtifactImageTile
                  key={key}
                  label={item.label}
                  subtitle={item.subtitle}
                  source={item.source}
                  src={item.storySrc}
                  alt={item.label}
                  href={item.href}
                />
              )
            }
            if (mk === 'video' && item.storySrc) {
              return (
                <ArtifactVideoTile
                  key={key}
                  label={item.label}
                  subtitle={item.subtitle}
                  source={item.source}
                  src={item.storySrc}
                />
              )
            }
            return (
              <ArtifactStoryPlaceholder
                key={key}
                label={item.label}
                subtitle={item.subtitle}
                source={item.source}
                href={item.href}
              />
            )
          }
          case 'gallery':
            return (
              <ArtifactGalleryTile
                key={key}
                label={item.label}
                subtitle={item.subtitle}
                source={item.source}
                previewSrc={item.previewSrc}
                count={item.count}
                onActivate={item.onActivate || scrollToRoll}
              />
            )
          case 'file':
            return (
              <ArtifactFileTile
                key={key}
                label={item.label}
                subtitle={item.subtitle}
                source={item.source}
                href={item.href}
                icon={item.icon}
              />
            )
          case 'link':
            return (
              <ArtifactLinkTile
                key={key}
                label={item.label}
                subtitle={item.subtitle}
                source={item.source}
                href={item.href}
              />
            )
          default:
            return null
        }
      })}
    </div>
  )
}
